import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import esLocale from '@fullcalendar/core/locales/es';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { getUser } from '../../shared/auth-service';
import Tooltip from '@mui/material/Tooltip';

import {
  Typography,
  Grid,
  Box,
  Avatar,
  Card,
  CardContent,
  Paper,
  Button,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Login as EntryIcon,
  Logout as ExitIcon,
} from '@mui/icons-material';
import { AccessTime } from '@mui/icons-material';
import BackDrop from '../../shared/backdropDE';
import { getEmployeeWorkShift } from './logic';

const EmployeeWorkShift = ({ isDrawerOpen }) => {
  const [loading, setLoading] = useState(true);
   const calendarRef = useRef(null);
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const isMobileView = useMediaQuery('(max-width: 600px)');
  const user = getUser();
  const today = new Date();
  const startLimit = new Date(today.getFullYear(), today.getMonth() - 3, 1); // 3 meses atrás
  const endLimit = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Fin del mes actual

   useEffect(() => {
        setTimeout(() => {
            const calendarApi = calendarRef.current?.getApi?.();
            if (calendarApi) {
                calendarApi.updateSize();
            }
        }, 300);
    }, [isDrawerOpen]);

    useEffect(() => {
        const resizeHandler = () => {
            setTimeout(() => {
                const calendarApi = calendarRef.current?.getApi?.();
                if (calendarApi) calendarApi.updateSize();
            }, 300);
        };
        window.addEventListener('resize', resizeHandler);
        window.addEventListener('orientationchange', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('orientationchange', resizeHandler);
        };
    }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getEmployeeWorkShift(user.employeeId);
       // console.log("Data received from getEmployeeWorkShift:", data);

        const events = data.flatMap((shift) => {
          const shiftDate = shift.shiftDate || '';
          const entryTime = shift.realTimeIn ? shift.realTimeIn.split(" ")[1].slice(0, 5) : '--:--';
          const exitTime = shift.realTimeOut ? shift.realTimeOut.split(" ")[1].slice(0, 5) : '--:--';

          const shiftCodeEvent = {
            title: shift.shiftCode || 'Sin código',
            start: shiftDate,
            backgroundColor: '#E0E0E0', // gris
            textColor: '#424242',
            borderColor: 'transparent',
            extendedProps: {
              type: 'ShiftCode',
              shiftCode: shift.shiftCode,
              shiftDescription: shift.shiftDescription,
              shiftDate,
              realTimeIn: entryTime,
              realTimeOut: exitTime,
              icon: isMobileView ? <AccessTime style={{ color: '#424242', fontSize: '1.2rem' }} /> : null
            }
          };
          
          const entryEvent = {
            title: entryTime,
            start: shiftDate,
            backgroundColor: '#D6EAF8', // azul claro
            textColor: '#1A5276',
            borderColor: 'transparent',
            extendedProps: {
              type: 'Entrada',
              icon: <EntryIcon style={{ color: '#1A5276', fontSize: '1.2rem' }} />,
              time: entryTime,
              shiftDate,
            }
          };
          
          const exitEvent = {
            title: exitTime,
            start: shiftDate,
            backgroundColor: '#D6EAF8', // azul claro
            textColor: '#1A5276',
            borderColor: 'transparent',
            extendedProps: {
              type: 'Salida',
              icon: <ExitIcon style={{ color: '#1A5276', fontSize: '1.2rem' }} />,
              time: exitTime,
              shiftDate,
            }
          };
          
          return [shiftCodeEvent, entryEvent, exitEvent];
        });

        setShifts(events);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchData();
  }, [isMobileView]);

  const handleEventClick = (info) => {
    const clickedDate = info.event.startStr;
  
    // Filtrar todos los eventos de ese mismo día
    const dayEvents = shifts.filter(e => e.start === clickedDate);
  
    // Extraer los datos necesarios
    const shiftCodeEvent = dayEvents.find(e => e.extendedProps.type === 'ShiftCode');
    const entryEvent = dayEvents.find(e => e.extendedProps.type === 'Entrada');
    const exitEvent = dayEvents.find(e => e.extendedProps.type === 'Salida');
  
    setSelectedShift({
      shiftDate: clickedDate,
      shiftCode: shiftCodeEvent?.extendedProps.shiftCode || 'Sin código',
      shiftDescription: shiftCodeEvent?.extendedProps.shiftDescription || 'Sin descripción',
      realTimeIn: entryEvent?.extendedProps.time || '--:--',
      realTimeOut: exitEvent?.extendedProps.time || '--:--',
    });
  };
  

  if (loading) return <BackDrop />;

  return (
    <Grid
      item
      xs={12}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        margin: "auto",
        marginTop: "-4rem",
        backgroundColor: "#FFF",
        paddingTop: "4rem",
        borderRadius: ".5rem",
        color: "#000000"
      }}
    >
      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        ref={calendarRef}
        locale={esLocale}
        events={shifts}
        eventClick={handleEventClick}
        height={800}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: ''
        }}
        titleFormat={{ year: 'numeric', month: 'long' }}
        eventOrder={(a, b) => a.extendedProps.type === 'ShiftCode' ? -1 : 1}
        eventContent={(eventInfo) => (
          <Box display="flex" alignItems="center" justifyContent="center">
            {eventInfo.event.extendedProps.icon}
            <Typography
              variant="body2"
              sx={{
                display:
                  eventInfo.event.extendedProps.type === 'ShiftCode'
                    ? isMobileView
                      ? 'none'
                      : 'block'
                    : isMobileView
                      ? 'none'
                      : 'block',
                marginLeft: eventInfo.event.extendedProps.icon ? '4px' : '0',
              }}
            >
              {eventInfo.event.title}
            </Typography>
          </Box>
        )}
        validRange={{
          start: startLimit.toISOString().split('T')[0],
          end: endLimit.toISOString().split('T')[0]
        }}
      />


      {selectedShift && (
        <Dialog open={Boolean(selectedShift)} onClose={() => setSelectedShift(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography style={{ color: '#00B0EF', fontWeight: 'bold', fontSize: isMobileView ? '20px' : '24px' }}>
              Turno <span style={{ color: '#1A3072' }}>Programado</span>
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Card variant="outlined" style={{ width: '100%', marginBottom: '16px', padding: isMobileView ? '8px' : '16px' }}>
              <Grid container alignItems="center">
                <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: '#00B0EF',
                      width: isMobileView ? 40 : 56,
                      height: isMobileView ? 40 : 56,
                      mr: 1
                    }}
                  >
                    {new Date(selectedShift.shiftDate).getDate()}
                  </Avatar>
                  <Box>
                    <Typography variant={isMobileView ? 'h6' : 'h5'} component="div">
                      {new Date(selectedShift.shiftDate).toLocaleDateString('es-ES', { weekday: 'long' }).charAt(0).toUpperCase() + new Date(selectedShift.shiftDate).toLocaleDateString('es-ES', { weekday: 'long' }).slice(1)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(selectedShift.shiftDate).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h3" component="div" style={{ color: '#1A3072', fontSize: isMobileView ? '1.5rem' : '2rem' }}>
                    {selectedShift.shiftDescription}
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            {/* Distribución del turno actualizada */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" style={{ color: '#1a3072', fontWeight: 'bold' }}>
                  Marcaciones
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: isMobileView ? 1 : 2,
                    borderRadius: '8px',
                    boxShadow: isMobileView ? 'none' : '0px 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection={isMobileView ? "column" : "row"}
                    alignItems="center"
                    justifyContent="center"
                    gap={isMobileView ? 2 : 4}
                    mt={2}
                  >
                  {/* Entrada */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width={isMobileView ? "100%" : "45%"}
                    mb={isMobileView ? 1 : 0}
                  >
                    <Tooltip title="Ingreso">
                      <Avatar
                        sx={{
                          bgcolor: '#00B0EF',
                          width: isMobileView ? 32 : 48,
                          height: isMobileView ? 32 : 48,
                          mr: 1
                        }}
                      >
                        <EntryIcon sx={{ fontSize: isMobileView ? '1rem' : '1.5rem' }} />
                      </Avatar>
                    </Tooltip>
                    <Box textAlign="center">
                      <Typography variant="h6" fontSize={isMobileView ? '0.9rem' : '1.25rem'}>
                        {selectedShift.realTimeIn || '--:--'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">Marcación Biométrica</Typography>
                    </Box>
                  </Box>


                    {/* Salida */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={isMobileView ? "100%" : "45%"}
                    >
                      <Tooltip title="Salida">
                        <Avatar
                          sx={{
                            bgcolor: '#00B0EF',
                            width: isMobileView ? 32 : 48,
                            height: isMobileView ? 32 : 48,
                            mr: 1
                          }}
                        >
                          <ExitIcon sx={{ fontSize: isMobileView ? '1rem' : '1.5rem' }} />
                        </Avatar>
                      </Tooltip>
                      <Box textAlign="center">
                        <Typography variant="h6" fontSize={isMobileView ? '0.9rem' : '1.25rem'}>
                          {selectedShift.realTimeOut || '--:--'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Marcación Biométrica</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </CardContent>
            </Card>
          </DialogContent>
          <Box textAlign="right" my={2} mr={2}>
            <Button
              onClick={() => setSelectedShift(null)}
              variant="outlined"
              color="primary"
              endIcon={
                <Avatar
                  sx={{
                    bgcolor: '#1A3072',
                    color: '#FFFFFF',
                    width: 24,
                    height: 24,
                    fontSize: 'small'
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Avatar>
              }
              sx={{
                color: '#1A3072',
                borderColor: '#1A3072',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                  borderColor: '#1A3072'
                }
              }}
            >
              CERRAR
            </Button>
          </Box>
        </Dialog>
      )}
    </Grid>
  );
};

export default EmployeeWorkShift;
